import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Form, Stack } from 'react-bootstrap';
import { UserContext } from '../../../../context/user_context/UserContext';
import { Loading } from '../../../../components/loading/Loading';
import moment from 'moment';
import { IconArrowLeft, IconArrowRight, IconDeviceFloppy, IconTextPlus, IconX } from '@tabler/icons-react';
import { addTimeSheet, fetchTimeSheetsForWeek } from '../../../../repository/TimeSheetDAO';
import { OrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { TableBody, TableCell, TableHead, TableRow, Table, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';

function TimeSheet() {
    const { firebaseUser } = useContext(UserContext);
    const { organization } = useContext(OrganizationContext)
    const [weeklyData, setWeeklyData] = useState();
    const [currentDate, setCurrentDate] = useState(moment());
    const [existingRows, setExistingRows] = useState([]);
    const [jobCodes, setJobCodes] = useState([]);
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const { t } = useTranslation();

    useEffect(() => {
        if(firebaseUser && currentDate) {
            setLoadingData(true)
            const week = currentDate.clone().startOf('week')
            const weekNumber = week.isoWeek();
            const formattedWeekNumber = `W${weekNumber.toString().padStart(2, '0')}`;
            const weekStartingDate = week.format('YYYY-MM-DD');
            fetchTimeSheetsForWeek(firebaseUser, `${weekStartingDate}-${formattedWeekNumber}-${firebaseUser.uid}`, weekStartingDate).then(setWeeklyData);
            setLoadingData(false)
        }
    }, [firebaseUser, currentDate]);

    useEffect(() => {
        if (weeklyData && weeklyData.daily_hours) {
            const tableData = restructureData(weeklyData.daily_hours);
            setExistingRows(tableData);
        } else {
            setExistingRows([])
        }
    }, [weeklyData]);

    useEffect(() => {
        if (organization) {
            setJobCodes(organization.job_codes)
        }
    }, [organization]);

    const restructureData = (daily_hours) => {
        const tableData = [];
        const uniqueJobCodes = new Set();
        for (const date in daily_hours) {
            for (const jobCode in daily_hours[date]) {
                uniqueJobCodes.add(jobCode);
            }
        }

        uniqueJobCodes.forEach((jobCode) => {
            const row = { jobCode };
            for (const date in daily_hours) {
                row[date] = daily_hours[date][jobCode] || '';
            }
            tableData.push(row);
        });

        return tableData;
    };

    const handleJobCodeChange = (rowIndex, newJobCode) => {
        setExistingRows((prevRows) => {
            const newRows = [...prevRows];
            newRows[rowIndex]= {"jobCode": newJobCode};
            return newRows;
        });
    };

    const calculateTotalHoursPerDay = () => {
        const totals = {};
        existingRows.forEach(row => {
            Object.keys(row).forEach(key => {
                if (key !== 'jobCode') {
                    totals[key] = (totals[key] || 0) + parseFloat(row[key] || 0);
                }
            });
        });
        return totals;
    };

    const calculateGrandTotalHours = (totalHoursPerDay) => {
        return Object.values(totalHoursPerDay).reduce((acc, currentValue) => acc + currentValue, 0);
    };

    const handleHoursChange = (rowIndex, date, hours) => {
        setExistingRows((prevRows) => {
            const newRows = [...prevRows];
            newRows[rowIndex][date] = parseFloat(hours) || 0;
            return newRows;
        });
    };

    const saveTimeSheets = async () => {

        const dailyHours = transformToDailyHours(existingRows);
        const week = currentDate.clone().startOf('week');
        const weekNumber = week.isoWeek();
        const formattedWeekNumber = `W${weekNumber.toString().padStart(2, '0')}`;
        const weekStartingDate = week.format('YYYY-MM-DD');
        const totalHours = calculateGrandTotalHours(calculateTotalHoursPerDay());
    
        const timeSheetDocument = {
            id: `${weekStartingDate}-${formattedWeekNumber}-${firebaseUser.uid}`,
            daily_hours: dailyHours,
            week_starting: weekStartingDate,
            employee_id: firebaseUser.uid,
            total_hours: totalHours
        };
    
        try {
            setLoadingData(true);
            const result = await addTimeSheet(firebaseUser.organization, timeSheetDocument);
    
            if (result.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                console.error('Error saving time sheet:', result.error);
            }
        } catch (error) {
            console.error('Unexpected error saving time sheet:', error);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    
    const submitTimeSheet = async () => {
        const dailyHours = transformToDailyHours(existingRows);
        const week = currentDate.clone().startOf('week')
        const weekNumber = week.isoWeek();
        const formattedWeekNumber = `W${weekNumber.toString().padStart(2, '0')}`;
        const weekStartingDate = week.format('YYYY-MM-DD');
        const totalHours = calculateGrandTotalHours(calculateTotalHoursPerDay());
      
        const timeSheetDocument = {
          id: `${weekStartingDate}-${formattedWeekNumber}-${firebaseUser.uid}`,
          daily_hours: dailyHours,
          week_starting: weekStartingDate,
          employee_id: firebaseUser.uid,
          total_hours: totalHours,
          status: "Submitted"
        };

        try {
            setLoadingData(true)
            await addTimeSheet(firebaseUser.organization, timeSheetDocument, showToast); 
            setLoadingData(false)
        } catch (error) {
            console.error('Error saving time sheet:', error);
        }
    }

    const transformToDailyHours = (rows) => {
        const dailyHours = {};
      
        rows.forEach(row => {
          Object.keys(row).forEach(date => {
            if (date !== 'jobCode' && row[date] !== "") {
              if (!dailyHours[date]) {
                dailyHours[date] = {};
              }
              dailyHours[date][row.jobCode] = parseFloat(row[date]) || 0;
            }
          });
        });
      
        return dailyHours;
      };

    const renderDateHeader = () => {
        const startOfWeek = currentDate.clone().startOf('week');
        const endOfWeek = currentDate.clone().endOf('week');
        const days = [<TableCell key="job-code" style={{ fontWeight: 'bold', width: '200px' }}>{t(StringKeys.JOB_CODE)}</TableCell>];
        let day = startOfWeek;
        while (day <= endOfWeek) {
            days.push(
                <TableCell key={day.format('YYYY-MM-DD')} style={{ fontWeight: 'bold', textAlign: "center" }}>
                    {day.format('M/D - ddd')}
                </TableCell>
            );
            day = day.clone().add(1, 'day');
        }
        days.push(
            <TableCell key="total" style={{ fontWeight: 'bold', textAlign: "center" }}>
                
            </TableCell>
        )
    
        return days;
    };
    

    const deleteRow = (rowIndex) => {
        setExistingRows((prevRows) => prevRows.filter((_, index) => index !== rowIndex));
    };

    const renderTableRows = () => {

        const totalHoursPerDay = calculateTotalHoursPerDay();
        const grandTotalHours = calculateGrandTotalHours(totalHoursPerDay);

        const sortedRows = existingRows.sort((a, b) => {
            const jobCodeA = jobCodes[a.jobCode];
            const jobCodeB = jobCodes[b.jobCode];

            // Ensure job codes exist in the jobCodes object before comparing
            if (!jobCodeA && jobCodeB) return 1; // Sort undefined jobCodeA to the end
            if (jobCodeA && !jobCodeB) return -1; // Sort undefined jobCodeB to the end
            if (!jobCodeA && !jobCodeB) return 0; // Keep original order if both are undefined

            const nameA = jobCodeA.toUpperCase();
            const nameB = jobCodeB.toUpperCase();

            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });

    
        const rows = sortedRows.map((row, index) => (
            <TableRow key={index}>
                <TableCell style={{ width: '200px' }}>
                    {row.jobCode}
                </TableCell>
                {renderDateHeader().slice(1, -1).map((day, dayIndex) => (
                    <TableCell key={dayIndex}>
                        <Form.Control
                            type="number"
                            value={row[day.key]}
                            onChange={(e) => handleHoursChange(index, day.key, e.target.value)}
                        />
                    </TableCell>
                ))}
                <TableCell>
                    <Button variant="danger" onClick={() => deleteRow(index)}><IconX/></Button>
                </TableCell>
            </TableRow>
        ));

        rows.push(
            <>
            {Object.keys(jobCodes).filter(jobCode => !existingRows.some(r => r.jobCode === jobCode)).length > 0 ? 
                (
                    <TableRow key="new-row">
                    <TableCell colSpan={2}>
                        <Form.Select
                            value=""
                            onChange={(e) => handleJobCodeChange(existingRows.length, e.target.value)}
                        >
                            <option value="">{t(StringKeys.SELECT_JOB_CODE)}</option>
                            {
                                Object.keys(jobCodes)
                                .filter(jobCode => !existingRows.some(row => row.jobCode === jobCode))
                                .sort((a, b) => jobCodes[a].localeCompare(jobCodes[b])) // Sorting alphabetically by value
                                .map(jobCode => (
                                    <option key={jobCode} value={jobCode}>
                                        {jobCodes[jobCode]}
                                    </option>
                                ))
                            }
                        </Form.Select>
                    </TableCell>
                    <TableCell colSpan={8}>
                        
                    </TableCell>
                </TableRow>
                ) : 
                (
                    null
                )
            }
            </>
        );    

        rows.push(
            <TableRow key="totals">
                <TableCell>{t(StringKeys.TOTAL_HOURS)}</TableCell>
                {renderDateHeader().slice(1, -1).map((day, dayIndex) => (
                    <TableCell key={dayIndex}>
                        {totalHoursPerDay[day.key] || 0}
                    </TableCell>
                ))}
                <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>{grandTotalHours}</TableCell>
            </TableRow>
        );
        return rows;
    };

    const previousWeek = () => {
        setExistingRows([])
        setCurrentDate(prevDate => prevDate.clone().subtract(1, 'week'));
    };

    const nextWeek = () => {
        setExistingRows([])
        setCurrentDate(prevDate => prevDate.clone().add(1, 'week'));
    };

    return (
        <>
            {weeklyData && organization && jobCodes ? (
                <>
                    <AppSidebar
                        content={
                            <>
                                <Stack gap={3}>
                                    <div></div>
                                    <div>
                                        <Row>
                                            <Col sm><h2>{t(StringKeys.TIME_SHEET)} - {firebaseUser.first_name} {firebaseUser.last_name}</h2></Col>
                                            <Col sm style={{textAlign:"end"}}>
                                                <Button style={{marginRight:"10px"}} onClick={previousWeek}><IconArrowLeft/>{t(StringKeys.PREVIOUS)}</Button>
                                                <Button onClick={nextWeek}>{t(StringKeys.NEXT)}<IconArrowRight/></Button>
                                            </Col>
                                        </Row>
                                    </div>
                                    <Table striped bordered hover>
                                        <TableHead className="users-table-header">
                                            <TableRow>{renderDateHeader()}</TableRow>
                                        </TableHead>
                                        <TableBody>{renderTableRows()}</TableBody>
                                    </Table>

                                    <Row>
                                        <Col sm></Col>
                                        <Col sm style={{textAlign:"end"}}>
                                        {weeklyData?.status === 'Submitted' || weeklyData?.status === 'Approved' ?
                                        (
                                            null
                                        )
                                        :
                                        (
                                            <Button variant="contained" style={{marginRight:"10px"}} onClick={saveTimeSheets}><IconDeviceFloppy/> {t(StringKeys.SAVE)}</Button>

                                        )
                                        
                                        }
                                            <Button variant="contained" onClick={submitTimeSheet} disabled={weeklyData?.status === 'Submitted' || weeklyData?.status === 'Approved'}>
                                            <IconTextPlus/> {t(StringKeys.SUBMIT)}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Stack>
                            </>
                        }
                    />
                </>
            ) :
                <AppSidebar
                    content={
                    <>
                        <Loading />
                    </>
                    }
                />
            }
        </>
    );
}

export default TimeSheet;